import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { listContactRequests, resolveContactRequest } from '../../service'
import { apiErrorText } from '../../service/coaching'
import { CONTACT_REASONS } from '../../constants/legal'

const reasonLabel = value => CONTACT_REASONS.find(reason => reason.value === value)?.label || value
const dateLabel = value => {
  const parsed = new Date(value)
  return value && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleString() : '-'
}
// Visitors type the link; only http(s) links become clickable.
const safeHref = value => /^https?:\/\//i.test(value || '') ? value : null

function ContentLink ({ value }) {
  if (!value) return '-'
  const href = safeHref(value)
  return href
    ? <Link href={href} target="_blank" rel="noopener noreferrer nofollow" sx={{ wordBreak: 'break-all' }}>{value}</Link>
    : <Typography variant="body2" component="span" sx={{ wordBreak: 'break-all' }}>{value}</Typography>
}

ContentLink.propTypes = {
  value: PropTypes.string
}

// Messages from the public contact form (/contact): takedown, removal and deletion requests and
// account questions. Resolving a message only marks it done; deleting content stays a manual step.
export default function ContactRequestsPanel () {
  const [rows, setRows] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState(null)
  const [message, setMessage] = useState(null)

  const load = useCallback(async () => {
    try {
      setError('')
      const result = await listContactRequests({ limit: 200 })
      setRows(Array.isArray(result) ? result : [])
    } catch (err) {
      setError('Contact requests could not be loaded.')
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const resolve = async row => {
    setBusyId(row.id)
    setMessage(null)
    try {
      const updated = await resolveContactRequest(row.id)
      setRows(current => current.map(item => item.id === row.id ? updated : item))
      setMessage({ severity: 'success', text: `Marked the message from ${row.email} as resolved.` })
    } catch (err) {
      setMessage({ severity: 'error', text: apiErrorText(err, 'The message could not be marked as resolved. Retry in a moment.') })
    } finally {
      setBusyId(null)
    }
  }

  const open = rows.filter(row => row.status !== 'resolved').length

  return (
    <Paper component="section" variant="outlined" aria-label="Contact requests" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="h2">Contact requests</Typography>
          <Typography variant="body2" color="text.secondary">
            Messages sent through the public contact form, newest first{loaded && !error ? ` · ${open} open` : ''}.
            Reply from your own email; resolving only marks a message as done.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>
      {message ? <Alert severity={message.severity} sx={{ mt: 1.5 }}>{message.text}</Alert> : null}
      {error ? <Alert severity="warning" sx={{ mt: 1.5 }}>{error}</Alert> : null}
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small" aria-label="Contact requests">
          <TableHead>
            <TableRow>
              <TableCell>Received</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>From</TableCell>
              <TableCell>Link</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateLabel(row.created_at)}</TableCell>
                <TableCell sx={{ minWidth: 160 }}>{reasonLabel(row.reason)}</TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  {row.name ? <Typography variant="body2">{row.name}</Typography> : null}
                  <Link href={`mailto:${row.email}`} sx={{ wordBreak: 'break-all' }}>{row.email}</Link>
                </TableCell>
                <TableCell sx={{ minWidth: 200, maxWidth: 280 }}><ContentLink value={row.content_link} /></TableCell>
                <TableCell sx={{ minWidth: 220, maxWidth: 420 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{row.message}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Chip
                    size="small"
                    label={row.status === 'resolved' ? 'Resolved' : 'New'}
                    color={row.status === 'resolved' ? 'default' : 'warning'}
                    variant={row.status === 'resolved' ? 'outlined' : 'filled'}
                  />
                  {row.status === 'resolved' && row.resolved_at
                    ? <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{dateLabel(row.resolved_at)}</Typography>
                    : null}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={row.status === 'resolved' || busyId === row.id}
                    onClick={() => resolve(row)}
                  >
                    Resolve
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {/* A failed load is not an empty list: never say there are no requests then. */}
            {loaded && !error && !rows.length
              ? <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No contact requests yet.</Typography>
                  </TableCell>
                </TableRow>
              : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
